import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { CalendarPlus, ChevronRight, ClipboardList, PoundSterling, Wrench } from "lucide-react";
import { listMyBookings } from "@/lib/bookings.functions";
import { StatusPill } from "@/components/StatusPill";
import { bookingStatusLabel, gbp, ukDate } from "@/lib/format";
import { AppHeader } from "@/components/site/AppHeader";
import { EmptyState, PageHeader, PageShell } from "@/components/ui/shields-layout";

const bookingsQuery = queryOptions({
  queryKey: ["my-bookings"],
  queryFn: () => listMyBookings(),
});

export const Route = createFileRoute("/_authenticated/bookings/")({
  head: () => ({
    meta: [
      { title: "Activity — Shields" },
      {
        name: "description",
        content: "See quotes, bookings, progress and completed work in one place.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookingsPage,
});

function BookingsPage() {
  const { data: bookings } = useSuspenseQuery(bookingsQuery);
  const [tab, setTab] = useState<"active" | "completed" | "cancelled">("active");
  const visible = bookings.filter((booking) => {
    if (tab === "completed") return ["completed", "closed"].includes(booking.status);
    if (tab === "cancelled") return ["cancelled", "declined", "expired"].includes(booking.status);
    return !["completed", "closed", "cancelled", "declined", "expired"].includes(
      booking.status,
    );
  });

  return (
    <div className="min-h-screen bg-background pb-28">
      <AppHeader workspace="customer" />

      <PageShell width="narrow">
        <PageHeader eyebrow="Activity" title="Your jobs" description="Quotes, agreed prices, progress and completed work." action={<Link to="/book" className="btn-hero"><CalendarPlus className="h-4 w-4" /> New request</Link>} />

        <div
          className="mt-6 grid grid-cols-3 gap-1 rounded-2xl border border-border bg-card p-1"
          role="tablist"
          aria-label="Booking status"
        >
          {(["active", "completed", "cancelled"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={
                "min-h-11 rounded-xl px-3 text-sm font-bold capitalize transition " +
                (tab === value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground")
              }
            >
              {value}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <EmptyState className="mt-6" icon={<ClipboardList className="h-6 w-6" />} title={`No ${tab} jobs`} description={`Your ${tab} work will appear here automatically.`} action={<Link to="/book" className="btn-hero">Book a Service</Link>} />
        ) : (
          <div className="mt-6 space-y-3">
            {visible.map((booking) => (
              <Link
                key={booking.id}
                to="/bookings/$bookingId"
                params={{ bookingId: booking.id }}
                className="flex min-h-24 items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-[0_8px_30px_rgba(6,27,58,.035)]"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  {booking.agreed_price_pence != null ? (
                    <PoundSterling className="h-5 w-5" />
                  ) : (
                    <Wrench className="h-5 w-5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <strong className="truncate text-base text-foreground">
                      {(booking.services as { name?: string } | null)?.name ?? "Service"}
                    </strong>
                    <StatusPill
                      status={booking.status}
                      label={bookingStatusLabel(booking.status)}
                    />
                  </span>
                  <small className="mt-1 block truncate text-sm text-muted-foreground">
                    {booking.postcode} · {ukDate(booking.created_at)}
                    {booking.agreed_price_pence != null
                      ? " · " + gbp(booking.agreed_price_pence)
                      : ""}
                  </small>
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </PageShell>
    </div>
  );
}
